"use client";

import { useState, useEffect, useRef } from "react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import CTA from "@/components/ui/CTA";
import Link from "next/link";
import Image from "next/image";
import { motion, AnimatePresence } from "framer-motion";
import { useMotion } from "@/contexts/MotionContext";
import { WORK_ITEMS } from "@/lib/content/work";
import HeroDraggableWindows from "@/components/home/HeroDraggableWindows";
import { LazyAsciiVideoCanvasBackgroundFixed } from "@/components/animations/LazyAsciiVideoCanvasBackground";
import DeliveryApproachSection from "@/components/sections/DeliveryApproachSection";
import { MEDIA } from "@/lib/assets";
import {
  HOME_GOOD_FIT_SECTION,
  HOME_SERVICE_CTA_FALLBACK,
  HOME_SERVICE_ROTATION_ITEMS,
  HOME_SERVICE_ROTATION_MS,
  HOME_SERVICES_SECTION_EYEBROW,
} from "@/lib/content/home-page-content";

const SERVICES = HOME_SERVICE_ROTATION_ITEMS;
const ROTATION_INTERVAL = HOME_SERVICE_ROTATION_MS;

export default function HomePage() {
  const { isMotionEnabled } = useMotion();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const currentService = SERVICES[currentIndex];
  const workExample = currentService.workCategory
    ? (() => {
        if (currentService.workCategory === "medical-devices") {
          const matches = WORK_ITEMS.filter(
            (item) =>
              item.listed &&
              item.categories?.includes(currentService.workCategory!),
          );
          const alcorMini = matches.find((item) => item.id === "alcor-ised");
          if (alcorMini) return alcorMini;
          const fluidlogic = matches.find((item) => item.id === "fluidlogic");
          if (fluidlogic) return fluidlogic;
          return matches[0] || null;
        }
        if (currentService.workCategory === "digital-experiences") {
          return (
            WORK_ITEMS.find(
              (item) =>
                item.listed &&
                item.categories?.includes(currentService.workCategory!) &&
                item.id !== "moneta",
            ) || null
          );
        }
        return (
          WORK_ITEMS.find(
            (item) =>
              item.listed &&
              item.categories?.includes(currentService.workCategory!),
          ) || null
        );
      })()
    : null;

  useEffect(() => {
    if (!isMotionEnabled || isPaused) return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % SERVICES.length);
    }, ROTATION_INTERVAL);
    return () => clearInterval(interval);
  }, [isMotionEnabled, isPaused]);

  return (
    <div className="relative isolate min-h-screen bg-tan dark:bg-background-dark">
      <LazyAsciiVideoCanvasBackgroundFixed
        videoSrc={MEDIA.heroCloudsVideo}
        posterSrc={MEDIA.heroCloudsPoster}
      />

      <div className="relative z-10 bg-transparent">
        <Nav />
        <div className="bg-transparent">
          <HeroDraggableWindows />

          <div className="relative z-20 mt-6 bg-tan shadow-[0_-12px_36px_rgba(0,0,0,0.06)] dark:bg-background-dark dark:shadow-[0_-12px_36px_rgba(0,0,0,0.25)] [@media(min-width:768px)_and_(max-width:1279px)]:mt-10 xl:mt-0">
            <section
              id="services"
              className="pt-12 pb-16 md:py-24"
              onMouseEnter={() => setIsPaused(true)}
              onMouseLeave={() => setIsPaused(false)}
            >
              <div className="container mx-auto px-4">
                <div className="max-w-7xl">
                  <p className="text-[11px] sm:text-xs md:text-sm font-semibold text-text-muted-light dark:text-text-muted-dark uppercase tracking-[0.12em] sm:tracking-[0.16em] max-w-xl sm:max-w-2xl leading-snug md:leading-relaxed mb-5 md:mb-12">
                    {HOME_SERVICES_SECTION_EYEBROW}
                  </p>
                  <div className="grid gap-8 xl:grid-cols-2 xl:items-start xl:gap-10 2xl:gap-14">
                    <div
                      role="tablist"
                      aria-label="Services"
                      className="hidden xl:block"
                    >
                      <div className="sticky top-24 space-y-4">
                        {SERVICES.map((service, index) => (
                          <motion.div
                            key={service.id}
                            layout
                            className="relative space-y-3"
                          >
                            <div
                              className={`absolute bottom-0 left-0 top-0 w-1 ${service.bgColor} ${
                                index === currentIndex
                                  ? "opacity-100"
                                  : "opacity-[0.28]"
                              }`}
                              aria-hidden
                            />
                            <button
                              onClick={() => setCurrentIndex(index)}
                              onKeyDown={(e) => {
                                if (e.key === "ArrowDown") {
                                  e.preventDefault();
                                  setCurrentIndex(
                                    (prev) => (prev + 1) % SERVICES.length,
                                  );
                                } else if (e.key === "ArrowUp") {
                                  e.preventDefault();
                                  setCurrentIndex(
                                    (prev) =>
                                      (prev - 1 + SERVICES.length) %
                                      SERVICES.length,
                                  );
                                } else if (e.key === "Home") {
                                  e.preventDefault();
                                  setCurrentIndex(0);
                                } else if (e.key === "End") {
                                  e.preventDefault();
                                  setCurrentIndex(SERVICES.length - 1);
                                }
                              }}
                              role="tab"
                              aria-selected={index === currentIndex}
                              aria-controls={`service-panel-desktop-${service.id}`}
                              id={`service-tab-${service.id}`}
                              tabIndex={index === currentIndex ? 0 : -1}
                              className={`relative w-full rounded-md py-1.5 text-left text-xl font-medium transition-all duration-200 cursor-pointer focus:outline-none focus:ring-2 focus:ring-brand-teal focus:ring-offset-2 dark:focus:ring-offset-background-dark ${
                                index === currentIndex
                                  ? "pl-6 text-text-light dark:text-text-dark"
                                  : "pl-6 text-gray-600 hover:bg-black/5 hover:text-gray-800 dark:text-gray-400 dark:hover:bg-white/10 dark:hover:text-gray-100"
                              }`}
                            >
                              {service.tabLabel}
                            </button>
                            <motion.div
                              id={`service-panel-desktop-${service.id}`}
                              role={
                                index === currentIndex ? "tabpanel" : undefined
                              }
                              aria-labelledby={`service-tab-${service.id}`}
                              aria-hidden={index !== currentIndex}
                              initial={false}
                              animate={
                                index === currentIndex
                                  ? { height: "auto", opacity: 1, y: 0 }
                                  : { height: 0, opacity: 0, y: -6 }
                              }
                              transition={{
                                height: {
                                  duration: 0.36,
                                  ease: [0.22, 1, 0.36, 1],
                                },
                                opacity: { duration: 0.2, ease: "easeOut" },
                                y: { duration: 0.2, ease: "easeOut" },
                              }}
                              className="overflow-hidden pl-7"
                            >
                              <div className="space-y-5 pb-1 pt-0.5">
                                <h2 className="text-[clamp(2rem,2.7vw,2.9rem)] font-medium leading-[1.08] text-text-light dark:text-text-dark">
                                  {service.href ? (
                                    <Link
                                      href={service.href}
                                      className="hover:opacity-70 transition-opacity"
                                    >
                                      {service.title}
                                    </Link>
                                  ) : (
                                    service.title
                                  )}
                                </h2>
                                <p className="text-lg font-light leading-relaxed text-text-muted-light dark:text-text-dark/90">
                                  {service.tagline}
                                </p>
                                <div className="pt-1">
                                  <CTA href={service.href || "/contact"}>
                                    {service.href
                                      ? service.ctaLabel
                                      : HOME_SERVICE_CTA_FALLBACK}
                                  </CTA>
                                </div>
                              </div>
                            </motion.div>
                          </motion.div>
                        ))}
                      </div>
                    </div>

                    <div
                      role="tablist"
                      aria-label="Services"
                      className="mb-6 xl:hidden"
                    >
                      <div className="flex gap-6 sm:gap-10">
                        {SERVICES.map((service, index) => (
                          <button
                            key={service.id}
                            onClick={() => setCurrentIndex(index)}
                            onKeyDown={(e) => {
                              if (e.key === "ArrowRight") {
                                e.preventDefault();
                                setCurrentIndex(
                                  (prev) => (prev + 1) % SERVICES.length,
                                );
                              } else if (e.key === "ArrowLeft") {
                                e.preventDefault();
                                setCurrentIndex(
                                  (prev) =>
                                    (prev - 1 + SERVICES.length) %
                                    SERVICES.length,
                                );
                              } else if (e.key === "Home") {
                                e.preventDefault();
                                setCurrentIndex(0);
                              } else if (e.key === "End") {
                                e.preventDefault();
                                setCurrentIndex(SERVICES.length - 1);
                              }
                            }}
                            role="tab"
                            aria-selected={index === currentIndex}
                            aria-controls={`service-panel-${service.id}`}
                            id={`service-tab-mobile-${service.id}`}
                            tabIndex={index === currentIndex ? 0 : -1}
                            className={`relative rounded-md px-1 pb-3 text-xl font-medium transition-all duration-200 cursor-pointer focus:outline-none focus:ring-2 focus:ring-brand-teal focus:ring-offset-2 dark:focus:ring-offset-background-dark ${
                              index === currentIndex
                                ? "text-text-light dark:text-text-dark"
                                : "text-gray-600 dark:text-gray-400"
                            }`}
                          >
                            {service.tabLabel}
                            {index === currentIndex && (
                              <motion.div
                                layoutId="activeTabIndicatorMobile"
                                className={`absolute bottom-0 left-0 right-0 h-1 ${service.bgColor} rounded-full`}
                                transition={{
                                  type: "spring",
                                  stiffness: 300,
                                  damping: 30,
                                }}
                              />
                            )}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="xl:hidden">
                      <AnimatePresence mode="wait">
                        {SERVICES.map(
                          (service, index) =>
                            index === currentIndex && (
                              <motion.div
                                key={service.id}
                                id={`service-panel-${service.id}`}
                                role="tabpanel"
                                aria-labelledby={`service-tab-${service.id}`}
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.3 }}
                              >
                                <div className="space-y-6">
                                  <h2 className="text-[clamp(1.9rem,8vw,2.75rem)] font-medium leading-[1.08] text-text-light dark:text-text-dark md:text-5xl md:leading-tight">
                                    {service.href ? (
                                      <Link
                                        href={service.href}
                                        className="hover:opacity-70 transition-opacity"
                                      >
                                        {service.title}
                                      </Link>
                                    ) : (
                                      service.title
                                    )}
                                  </h2>
                                  <p className="max-w-2xl text-xl font-light leading-relaxed text-text-muted-light dark:text-text-dark/90">
                                    {service.tagline}
                                  </p>
                                  <p className="text-lg text-text-muted-light dark:text-text-muted-dark font-light leading-relaxed max-w-2xl">
                                    {service.description}
                                  </p>
                                  <div className="pt-4">
                                    <CTA href={service.href || "/contact"}>
                                      {service.href
                                        ? service.ctaLabel
                                        : HOME_SERVICE_CTA_FALLBACK}
                                    </CTA>
                                  </div>
                                  {workExample && (
                                    <div className="pt-8 xl:hidden">
                                      <ServiceHeroWorkPreview
                                        item={workExample}
                                        isMotionEnabled={isMotionEnabled}
                                      />
                                    </div>
                                  )}
                                </div>
                              </motion.div>
                            ),
                        )}
                      </AnimatePresence>
                    </div>

                    <div className="hidden xl:block">
                      <AnimatePresence mode="wait">
                        {workExample && (
                          <motion.div
                            key={currentService.id}
                            initial={{ opacity: 0, x: 24 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -24 }}
                            transition={{ duration: 0.3 }}
                          >
                            <ServiceHeroWorkPreview
                              item={workExample}
                              isMotionEnabled={isMotionEnabled}
                              className="max-w-none"
                              mediaClassName="aspect-[16/10] 2xl:aspect-[16/9]"
                              mediaSizes="(max-width: 1536px) 62vw, 58vw"
                            />
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <DeliveryApproachSection />

            <section className="py-24 pb-32">
              <div className="container mx-auto px-4">
                <div className="max-w-7xl">
                  <div className="rounded-2xl border-2 border-brand-yellow/20 bg-brand-yellow/50 card-texture-surface p-6 text-text-muted-light dark:border-brand-yellow/30 dark:bg-background-dark dark:text-text-dark/90 sm:p-8 md:p-12">
                    <h2 className="mb-8 text-3xl font-medium leading-snug text-text-light dark:text-text-dark sm:text-4xl md:mb-12 md:text-5xl md:leading-normal">
                      {HOME_GOOD_FIT_SECTION.heading}
                    </h2>
                    <div className="grid gap-10 md:grid-cols-2 md:gap-12 lg:gap-16">
                      <div>
                        <ul className="list-disc space-y-3 pl-5 text-base font-light leading-relaxed marker:text-current md:space-y-4 md:pl-6 md:text-lg">
                          {HOME_GOOD_FIT_SECTION.bullets.map((line) => (
                            <li key={line}>{line}</li>
                          ))}
                        </ul>
                      </div>
                      <div className="space-y-6">
                        <p className="text-base font-light leading-relaxed text-text-light/95 dark:text-text-dark/90 md:text-lg">
                          {HOME_GOOD_FIT_SECTION.supporting}
                        </p>
                        <div className="md:flex md:justify-end lg:justify-start">
                          <CTA href={HOME_GOOD_FIT_SECTION.ctaHref}>
                            {HOME_GOOD_FIT_SECTION.ctaLabel}
                          </CTA>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>
        <div className="relative z-20 bg-tan dark:bg-background-dark">
          <Footer />
        </div>
      </div>
    </div>
  );
}

type ServiceHeroWorkItem = (typeof WORK_ITEMS)[number];

function ServiceHeroWorkPreview({
  item,
  isMotionEnabled,
  className,
  mediaClassName,
  mediaSizes,
}: {
  item: ServiceHeroWorkItem;
  isMotionEnabled: boolean;
  className?: string;
  mediaClassName?: string;
  mediaSizes?: string;
}) {
  const mediaBlock = (
    <div
      className={`relative mb-4 aspect-video overflow-hidden rounded-xl ${mediaClassName || ""}`}
    >
      <div
        className={`absolute inset-0 -m-2 rounded-xl opacity-20 ${item.slug ? "transition-opacity group-hover:opacity-30" : ""}`}
        style={{ backgroundColor: item.color }}
      />
      <div className="relative h-full w-full">
        {item.featuredMedia.type === "video" ? (
          <WorkVideo
            src={item.featuredMedia.url}
            isMotionEnabled={isMotionEnabled}
          />
        ) : (
          <Image
            src={item.featuredMedia.url}
            alt={item.title}
            fill
            className="object-cover"
            sizes={mediaSizes || "(max-width: 1280px) 100vw, 400px"}
          />
        )}
      </div>
    </div>
  );

  if (item.slug) {
    return (
      <Link
        href={`/work/${item.slug}`}
        className={`group block ${className || ""}`}
      >
        {mediaBlock}
        <div className="flex items-start justify-between gap-5">
          <h3 className="text-xl font-medium text-text-light transition-opacity group-hover:opacity-70 dark:text-text-dark">
            {item.title}
          </h3>
          <p className="pt-1 text-right text-sm text-text-muted-light dark:text-text-muted-dark">
            {item.client.name}
          </p>
        </div>
      </Link>
    );
  }

  return (
    <div className={className}>
      {mediaBlock}
      <div className="flex items-start justify-between gap-5">
        <h3 className="text-xl font-medium text-text-light dark:text-text-dark">
          {item.title}
        </h3>
        <p className="pt-1 text-right text-sm text-text-muted-light dark:text-text-muted-dark">
          {item.client.name}
        </p>
      </div>
    </div>
  );
}

function WorkVideo({
  src,
  isMotionEnabled,
}: {
  src: string;
  isMotionEnabled: boolean;
}) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [shouldLoad, setShouldLoad] = useState(false);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") {
      setShouldLoad(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setShouldLoad(true);
            break;
          }
        }
      },
      { rootMargin: "140px 0px", threshold: 0.01 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    const v = videoRef.current;
    if (!v || !shouldLoad) return;
    if (isMotionEnabled) {
      void v.play().catch(() => {});
    } else {
      v.pause();
      v.currentTime = 0;
    }
  }, [isMotionEnabled, shouldLoad]);

  return (
    <div ref={wrapRef} className="relative h-full min-h-0 w-full">
      {shouldLoad ? (
        <video
          ref={videoRef}
          src={src}
          className="h-full w-full object-cover"
          muted
          loop
          playsInline
          preload="none"
        />
      ) : (
        <div
          className="min-h-[8rem] h-full w-full bg-text-light/[0.05] dark:bg-white/[0.06]"
          aria-hidden
        />
      )}
    </div>
  );
}
